import React, {useCallback, useMemo, useState} from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {Box, Chip, Stack, Typography} from "@mui/material";
import {ComboBox} from "../../../comboBox/ComboBox.jsx";
import "react-toastify/dist/ReactToastify.css";
import "../../../../scss/PowerTabs.scss";
import {DEFAULT_DAAP_DATA_SOURCE, DEFAULT_DAAP_REPORT_TYPE} from "../../cns/constants.js";
import {useDaapCompanies, useDaapExistingDistributorPlatformSubscriptions} from "../hooks/DaapSubscription.hooks.jsx";
import DaapDistPlatSubPrevEdit from "./DaapDistPlatPrevEdit.jsx";

const buildSubscriptionId = (email, reportType, dataSource, company) => `${email}-${reportType}-${dataSource}-${company}`;

const daapCompanyDataSourceColorByValue = {
    KEHE: "secondary",
    UNFI: "info",
};

const renderDaapCompanyOption = (props, option) => (
    <Box
        component="li"
        {...props}
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
        }}
    >
        <Chip
            label={option.dataSource}
            size="small"
            variant="outlined"
            color={daapCompanyDataSourceColorByValue[option.dataSource] || "default"}
            sx={{minWidth: 58}}
        />
        <Typography variant="body2" component="span">
            {option.displayName}
        </Typography>
    </Box>
);

const DaapDistPlatSubForm = ({
                                 selectedCompany,
                                 selectedContactEmails = [],
                                 hasSelectedContacts,
                                 resetSubscriptionSelections,
                             }) => {
    const {
        control, getValues, setValue,
    } = useFormContext();

    const [ignoredDistributorPlatformRows, setIgnoredDistributorPlatformRows] = useState(new Set());
    const [removedDistributorPlatformRows, setRemovedDistributorPlatformRows] = useState(new Map());
    const {data: initialData, loading: isInitialDataLoading} = useDaapExistingDistributorPlatformSubscriptions(selectedContactEmails);
    const canSelectDistributorPlatformOptions = Boolean(selectedCompany) && Boolean(hasSelectedContacts);

    const distributorPlatformRows = useMemo(() => {
        const selectedContactEmailSet = new Set(selectedContactEmails);

        return (initialData?.sdnoDaapDistributorPlatformSubscription?.distributorPlatforms || []).map((subscription) => ({
            id: buildSubscriptionId(
                subscription.email,
                subscription.reportType,
                subscription.dataSource,
                subscription.company
            ),
            email: subscription.email,
            reportType: subscription.reportType,
            reportTypeDisplayName: subscription.reportTypeDisplayName,
            dataSource: subscription.dataSource,
            company: subscription.company,
        })).filter((row) => selectedContactEmailSet.has(row.email));
    }, [initialData, selectedContactEmails]);

    const selectedReport = useWatch({
        control, name: "selectedReport",
    }) || [];

    const selectedDataSource = useWatch({
        control, name: "selectedDataSource",
    }) || [];

    const selectedDaapCompany = useWatch({
        control, name: "selectedDaapCompany",
    }) || [];

    const selectedDataSourceValues = useMemo(() => {
        return selectedDataSource.map((dataSource) => dataSource.value.toUpperCase());
    }, [selectedDataSource]);

    const {data: daapCompaniesData, loading: isDaapCompaniesLoading} = useDaapCompanies(selectedDataSourceValues);

    const daapCompanyOptions = useMemo(() => {
        return (daapCompaniesData?.sdnoDaapCompanies || []).map(({company, dataSource}) => ({
            value: `${dataSource}:${company}`, displayName: `${company}`, company, dataSource,
        }));
    }, [daapCompaniesData]);

    const distributorPlatformDraft = useMemo(() => {
        const added = new Map();
        const initialDistributorPlatformRowIds = new Set(distributorPlatformRows.map((row) => row.id));

        if (!selectedCompany || selectedContactEmails.length === 0 || selectedReport.length === 0 || selectedDaapCompany.length === 0) {
            return {
                added, removed: removedDistributorPlatformRows,
            };
        }

        selectedContactEmails.forEach((email) => {
            selectedReport.forEach((report) => {
                selectedDaapCompany.forEach((daapCompany) => {
                    const subscriptionId = buildSubscriptionId(
                        email,
                        report.value,
                        daapCompany.dataSource,
                        daapCompany.company
                    );

                    if (initialDistributorPlatformRowIds.has(subscriptionId) || removedDistributorPlatformRows.has(subscriptionId) || ignoredDistributorPlatformRows.has(subscriptionId)) {
                        return;
                    }

                    added.set(subscriptionId, {
                        id: subscriptionId,
                        email,
                        reportType: report.value,
                        reportTypeDisplayName: report.displayName,
                        dataSource: daapCompany.dataSource,
                        company: daapCompany.company,
                        isNew: true,
                    });
                });
            });
        });

        return {
            added, removed: removedDistributorPlatformRows,
        };
    }, [
        selectedCompany,
        selectedContactEmails,
        selectedReport,
        selectedDaapCompany,
        ignoredDistributorPlatformRows,
        distributorPlatformRows,
        removedDistributorPlatformRows,
    ]);

    const distributorPlatformDraftRows = useMemo(() => {
        const baseRows = distributorPlatformRows.map((row) => ({
            ...row,
            isMarkedForDeletion: distributorPlatformDraft.removed.has(row.id),
        }));

        return [
            ...baseRows,
            ...Array.from(distributorPlatformDraft.added.values()),
        ];
    }, [distributorPlatformDraft, distributorPlatformRows]);

    const removeSubscription = useCallback((row) => {
        if (row.isNew) {
            setIgnoredDistributorPlatformRows((prev) => {
                const next = new Set(prev);
                next.add(row.id);

                return next;
            });
            return;
        }

        setRemovedDistributorPlatformRows((prev) => {
            const next = new Map(prev);

            if (next.has(row.id)) {
                next.delete(row.id);
            } else {
                next.set(row.id, row);
            }

            return next;
        });
    }, []);

    const resetDistributorPlatformSelections = useCallback(() => {
        setValue("selectedReport", []);
        setValue("selectedDataSource", []);
        setValue("selectedDaapCompany", []);
    }, [setValue]);

    const resetDraftState = useCallback(() => {
        setIgnoredDistributorPlatformRows(new Set());
        setRemovedDistributorPlatformRows(new Map());
    }, []);

    const resetAfterSave = useCallback(() => {
        setValue("selectedCompany", null);
        setValue("selectedContactEmails", []);
        (resetSubscriptionSelections || resetDistributorPlatformSelections)();
        resetDraftState();
    }, [resetDistributorPlatformSelections, resetDraftState, resetSubscriptionSelections, setValue]);

    return (<Stack spacing={3}>
        <Stack direction="row" spacing={2} sx={{width: "100%"}}>
            <Box sx={{flex: 1}}>
                <Controller
                    name="selectedReport"
                    control={control}
                    render={({field}) => (<ComboBox
                        isLoading={false}
                        label="Report Type *"
                        value={field.value || []}
                        disabled={!canSelectDistributorPlatformOptions}
                        style={{width: "100%"}}
                        filterOptions={{
                            limit: 1000, stringify: (option) => `${option.displayName} ${option.value}`,
                        }}
                        data={DEFAULT_DAAP_REPORT_TYPE}
                        isMultiple={true}
                        onChange={(_, value) => {
                            field.onChange(value || []);
                        }}
                        getOptionKeyImplementation={(option) => option.value}
                    />)}
                />
            </Box>

            <Box sx={{flex: 1}}>
                <Controller
                    name="selectedDataSource"
                    control={control}
                    render={({field}) => (<ComboBox
                        isLoading={false}
                        label="Data Source *"
                        value={field.value || []}
                        disabled={!canSelectDistributorPlatformOptions}
                        style={{width: "100%"}}
                        filterOptions={{
                            limit: 1000, stringify: (option) => `${option.displayName} ${option.value}`,
                        }}
                        data={DEFAULT_DAAP_DATA_SOURCE}
                        isMultiple={true}
                        onChange={(_, value) => {
                            const nextDataSources = value || [];
                            const nextDataSourceValues = new Set(nextDataSources.map((dataSource) => dataSource.value.toUpperCase()));
                            const selectedDaapCompanies = getValues("selectedDaapCompany") || [];

                            field.onChange(nextDataSources);
                            setValue("selectedDaapCompany", selectedDaapCompanies.filter((company) => nextDataSourceValues.has(company.dataSource)));
                        }}
                        getOptionKeyImplementation={(option) => option.value}
                    />)}
                />
            </Box>
        </Stack>

        <Controller
            name="selectedDaapCompany"
            control={control}
            render={({field}) => (<ComboBox
                isLoading={isDaapCompaniesLoading}
                label="DAAP Company *"
                value={field.value || []}
                filterOptions={{
                    limit: 1000, stringify: (option) => `${option.displayName} ${option.value}`,
                }}
                data={daapCompanyOptions}
                isMultiple={true}
                disabled={!canSelectDistributorPlatformOptions || selectedDataSourceValues.length === 0}
                renderOption={renderDaapCompanyOption}
                onChange={(_, value) => {
                    field.onChange(value || []);
                }}
                getOptionKeyImplementation={(option) => option.value}
            />)}
        />

        <DaapDistPlatSubPrevEdit
            selectedCompany={selectedCompany}
            selectedContactEmails={selectedContactEmails}
            distributorPlatformDraftRows={distributorPlatformDraftRows}
            distributorPlatformDraft={distributorPlatformDraft}
            deleteSubscription={removeSubscription}
            isInitialDataLoading={isInitialDataLoading}
            resetForm={resetAfterSave}
        />

    </Stack>);
};

export default DaapDistPlatSubForm;